import type { MessagePost, SentMessagePost } from "../../../../packages/shared/src/messages.ts";

export type MessageOutboxStatus = "waiting" | "sending" | "failed" | "sent";

export interface MessageOutboxItem {
  readonly accountId: string;
  readonly officerId: string;
  readonly threadId: string;
  readonly clientPostId: string;
  readonly body: string;
  readonly createdAt: string;
  readonly status: MessageOutboxStatus;
  readonly failureReason: string | null;
  readonly sentPost: SentMessagePost | null;
}
export function queueMessagePost(input: {
  readonly accountId: string;
  readonly officerId: string;
  readonly threadId: string;
  readonly clientPostId: string;
  readonly body: string;
  readonly createdAt: string;
  readonly networkOnline: boolean;
}): MessageOutboxItem {
  return {
    accountId: input.accountId,
    officerId: input.officerId,
    threadId: input.threadId,
    clientPostId: input.clientPostId,
    body: input.body.trim(),
    createdAt: input.createdAt,
    status: input.networkOnline ? "sending" : "waiting",
    failureReason: null,
    sentPost: null,
  };
}

export function outboxForThread(
  outbox: readonly MessageOutboxItem[],
  accountId: string,
  officerId: string,
  threadId: string,
): MessageOutboxItem[] {
  return outbox
    .filter((item) =>
      item.accountId === accountId && item.officerId === officerId && item.threadId === threadId
    )
    .sort((left, right) => Date.parse(left.createdAt) - Date.parse(right.createdAt));
}

export function nextMessageOutboxItem(
  outbox: readonly MessageOutboxItem[],
  accountId: string,
  officerId: string,
  networkOnline: boolean,
): MessageOutboxItem | null {
  if (!networkOnline) return null;
  return outbox.find((item) =>
    item.accountId === accountId &&
    item.officerId === officerId &&
    (item.status === "waiting" || item.status === "sending")
  ) ?? null;
}

function updateOutboxItem(
  outbox: readonly MessageOutboxItem[],
  clientPostId: string,
  update: (item: MessageOutboxItem) => MessageOutboxItem,
): MessageOutboxItem[] {
  return outbox.map((item) => item.clientPostId === clientPostId ? update(item) : item);
}

export function markOutboxSending(
  outbox: readonly MessageOutboxItem[],
  clientPostId: string,
): MessageOutboxItem[] {
  return updateOutboxItem(outbox, clientPostId, (item) => ({
    ...item,
    status: "sending",
    failureReason: null,
  }));
}

export function markOutboxFailed(
  outbox: readonly MessageOutboxItem[],
  clientPostId: string,
  reason: string,
): MessageOutboxItem[] {
  return updateOutboxItem(outbox, clientPostId, (item) => ({
    ...item,
    status: "failed",
    failureReason: reason,
  }));
}

export function markOutboxSent(
  outbox: readonly MessageOutboxItem[],
  clientPostId: string,
  sentPost: SentMessagePost,
): MessageOutboxItem[] {
  return updateOutboxItem(outbox, clientPostId, (item) => ({
    ...item,
    status: "sent",
    failureReason: null,
    sentPost,
  }));
}

export function retryOutboxItem(
  outbox: readonly MessageOutboxItem[],
  clientPostId: string,
  networkOnline: boolean,
): MessageOutboxItem[] {
  return updateOutboxItem(outbox, clientPostId, (item) => ({
    ...item,
    status: networkOnline ? "sending" : "waiting",
    failureReason: null,
  }));
}

export function reconcileMessageOutbox(
  outbox: readonly MessageOutboxItem[],
  posts: readonly Pick<MessagePost, "postId">[],
): MessageOutboxItem[] {
  const loaded = new Set(posts.map((post) => post.postId));
  return outbox.filter((item) => item.sentPost === null || !loaded.has(item.sentPost.postId));
}

export function messageOutboxStatusLine(
  item: MessageOutboxItem,
  formatWhen: (iso: string) => string,
): string {
  if (item.status === "waiting") return "Waiting for connection…";
  if (item.status === "failed") return "Not sent · Retry";
  const when = formatWhen(item.sentPost?.createdAt ?? item.createdAt);
  if (item.status === "sent") return when === "" ? "Sent" : `Sent · ${when}`;
  return "Sending…";
}
